import type { ThemeTokens, ContrastIssue } from '../types/theme';

export interface RGB {
  r: number;
  g: number;
  b: number;
}

/** Parse hex color string to RGB */
export function hexToRgb(hex: string): RGB {
  let cleaned = hex.trim().replace(/^#/, '');
  if (cleaned.length === 3) {
    cleaned = cleaned
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const num = parseInt(cleaned, 16);
  if (isNaN(num)) {
    return { r: 0, g: 0, b: 0 };
  }
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
}

/** Convert RGB to Hex */
export function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (val: number) => Math.max(0, Math.min(255, Math.round(val)));
  const toHex = (n: number) => clamp(n).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Calculate relative luminance following WCAG 2.1 specifications */
export function getLuminance(r: number, g: number, b: number): number {
  const [r0 = 0, g0 = 0, b0 = 0] = [r, g, b].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r0 + 0.7152 * g0 + 0.0722 * b0;
}

/** Calculate contrast ratio between two hex colors (1 to 21) */
export function getContrastRatio(hex1: string, hex2: string): number {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);
  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return Number(((brightest + 0.05) / (darkest + 0.05)).toFixed(2));
}

/** Get WCAG score tier from contrast ratio */
export function getContrastScore(ratio: number): 'AAA' | 'AA' | 'AA-Large' | 'Fail' {
  if (ratio >= 7.0) return 'AAA';
  if (ratio >= 4.5) return 'AA';
  if (ratio >= 3.0) return 'AA-Large';
  return 'Fail';
}

/**
 * Intelligently adjust foreground color to satisfy target contrast ratio (default 4.5 for AA)
 */
export function fixContrastColor(
  foregroundHex: string,
  backgroundHex: string,
  targetRatio = 4.5
): string {
  const currentRatio = getContrastRatio(foregroundHex, backgroundHex);
  if (currentRatio >= targetRatio) return foregroundHex;

  const bgRgb = hexToRgb(backgroundHex);
  const bgLum = getLuminance(bgRgb.r, bgRgb.g, bgRgb.b);
  const fgRgb = hexToRgb(foregroundHex);

  // Determine whether to lighten or darken
  const shouldLighten = bgLum < 0.5;

  // Step towards white or black
  for (let step = 1; step <= 25; step++) {
    const factor = step / 25;
    let r: number, g: number, b: number;

    if (shouldLighten) {
      r = fgRgb.r + (255 - fgRgb.r) * factor;
      g = fgRgb.g + (255 - fgRgb.g) * factor;
      b = fgRgb.b + (255 - fgRgb.b) * factor;
    } else {
      r = fgRgb.r * (1 - factor);
      g = fgRgb.g * (1 - factor);
      b = fgRgb.b * (1 - factor);
    }

    const candidate = rgbToHex(r, g, b);
    const ratio = getContrastRatio(candidate, backgroundHex);

    if (ratio >= targetRatio) {
      return candidate;
    }
  }

  // Fallback to pure white or dark black if tone interpolation didn't reach ratio
  return shouldLighten ? '#FFFFFF' : '#0F172A';
}

/** Critical foreground / background pairs to audit for contrast */
export const CRITICAL_CONTRAST_PAIRS: Array<{
  foregroundKey: keyof ThemeTokens;
  backgroundKey: keyof ThemeTokens;
  foregroundLabel: string;
  backgroundLabel: string;
  minRatio?: number;
}> = [
  {
    foregroundKey: 'sidebarText',
    backgroundKey: 'primarySidebarBackground',
    foregroundLabel: 'Sidebar Text',
    backgroundLabel: 'Primary Sidebar Background',
  },
  {
    foregroundKey: 'sidebarSecondaryText',
    backgroundKey: 'primarySidebarBackground',
    foregroundLabel: 'Sidebar Secondary Text',
    backgroundLabel: 'Primary Sidebar Background',
    minRatio: 3.0,
  },
  {
    foregroundKey: 'sidebarActiveText',
    backgroundKey: 'sidebarActiveBackground',
    foregroundLabel: 'Sidebar Active Item Text',
    backgroundLabel: 'Sidebar Active Item Background',
  },
  {
    foregroundKey: 'messageText',
    backgroundKey: 'chatBackground',
    foregroundLabel: 'Message Text',
    backgroundLabel: 'Chat Background',
  },
  {
    foregroundKey: 'messageSecondaryText',
    backgroundKey: 'chatBackground',
    foregroundLabel: 'Message Secondary Text',
    backgroundLabel: 'Chat Background',
    minRatio: 3.0,
  },
  {
    foregroundKey: 'primaryButtonText',
    backgroundKey: 'primaryButtonBackground',
    foregroundLabel: 'Primary Button Text',
    backgroundLabel: 'Primary Button Background',
  },
  {
    foregroundKey: 'secondaryButtonText',
    backgroundKey: 'secondaryButtonBackground',
    foregroundLabel: 'Secondary Button Text',
    backgroundLabel: 'Secondary Button Background',
  },
  {
    foregroundKey: 'mentionText',
    backgroundKey: 'mentionBackground',
    foregroundLabel: 'Mention Text',
    backgroundLabel: 'Mention Background',
  },
  {
    foregroundKey: 'composerText',
    backgroundKey: 'composerBackground',
    foregroundLabel: 'Composer Text',
    backgroundLabel: 'Composer Background',
  },
  {
    foregroundKey: 'tooltipText',
    backgroundKey: 'tooltipBackground',
    foregroundLabel: 'Tooltip Text',
    backgroundLabel: 'Tooltip Background',
  },
  {
    foregroundKey: 'codeBlockText',
    backgroundKey: 'codeBlockBackground',
    foregroundLabel: 'Code Block Text',
    backgroundLabel: 'Code Block Background',
  },
  {
    foregroundKey: 'inlineCodeText',
    backgroundKey: 'inlineCodeBackground',
    foregroundLabel: 'Inline Code Text',
    backgroundLabel: 'Inline Code Background',
  },
];

/** Audit a ThemeTokens object and return all failing or low-contrast pairs */
export function checkThemeContrast(tokens: ThemeTokens): ContrastIssue[] {
  const issues: ContrastIssue[] = [];

  for (const pair of CRITICAL_CONTRAST_PAIRS) {
    const fg = tokens[pair.foregroundKey];
    const bg = tokens[pair.backgroundKey];
    if (!fg || !bg) continue;

    const ratio = getContrastRatio(fg, bg);
    const minRatio = pair.minRatio || 4.5;

    if (ratio < minRatio) {
      issues.push({
        foregroundKey: pair.foregroundKey,
        backgroundKey: pair.backgroundKey,
        foregroundLabel: pair.foregroundLabel,
        backgroundLabel: pair.backgroundLabel,
        ratio,
        score: getContrastScore(ratio),
        suggestedColor: fixContrastColor(fg, bg, minRatio),
      });
    }
  }

  return issues;
}

/** Automatically fix all contrast issues in a theme token set */
export function autoFixThemeContrast(tokens: ThemeTokens): ThemeTokens {
  const updated = { ...tokens };
  const issues = checkThemeContrast(tokens);

  for (const issue of issues) {
    if (issue.suggestedColor) {
      (updated as any)[issue.foregroundKey] = issue.suggestedColor;
    }
  }

  return updated;
}
